import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Callout } from '@factorialco/deprecated-design-system'
import { type RecordType, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { LinkRemove, Plus, Target } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { UpsellingKit } from 'modules/upselling_kit/components'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import {
  AssignJobCatalogTreeNodeAttributesDocument,
  NodeCompetenciesDataCollectionDocument,
  type NodeCompetenciesDataCollectionNodeAttributeFragment,
  NodeCompetenciesForFinderDocument,
  NodeCompetenciesPageDocument,
} from 'generated/resources/graphql'
import { compact, groupBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import app from 'modules/core/store/models/app'

import {
  CompetenciesFinder,
  type Competency as CompetenciesFinderCompetency,
  type CompetencySuggestionTarget,
} from 'modules/competencies/components/CompetenciesFinder'
import {
  CompetencyLevelsAssignSidepanel,
  JobCatalogNodeAssignment,
} from 'modules/competencies/components/CompetencyLevelsAssignSidepanel'
import {
  type CompetencyDataCollectionItem,
  buildBulkAssignCompetencyAttributes,
  getCompetencyLevelsFromNodes,
  isCompetencyAttribute,
} from 'modules/job_catalog/lib/competencyUtils'
import {
  NODE_TYPES,
  NodeType,
  formatUuidToNodeType,
  getTextFromNodeType,
  getValidNodeIdFromParams,
  isNodeType,
  nodeTypeIcon,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import UnassignCompetencyModal from './UnassignModal'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'

interface AssignCompetencyLevelsParams {
  competencyId: string
  roleLevels: JobCatalogNodeAssignment[]
}

interface UnassignCompetencyParams {
  nodeAttributeId: string
  competencyName: string
}

interface CompetenciesPageContentProps {
  id: string
  rootId: string | undefined
}

const mapNodeAttributesToCompetencies = (
  attributes: NodeCompetenciesDataCollectionNodeAttributeFragment[],
  nodeId: string
) => {
  return attributes
    .filter(isCompetencyAttribute)
    .map(({ attributeId, definedAt, valueCompetency, id: nodeAttributeId, definedAtNode }) => {
      if (!valueCompetency) return null

      const normalizedType = normalizeNodeType(definedAt)
      const type: NodeType = isNodeType(normalizedType) ? normalizedType : NODE_TYPES.ROOT

      return {
        ...valueCompetency,
        id: String(attributeId),
        nodeAttributeId: nodeAttributeId,
        definedAt: definedAtNode.name || getTextFromNodeType(type),
        icon: nodeTypeIcon(type),
        readonly: normalizedType !== normalizeNodeType(nodeId),
      }
    })
    .filter((competency): competency is CompetencyDataCollectionItem => competency !== null)
}

const recordIsCompetency = (
  record: RecordType
): record is RecordType & CompetencyDataCollectionItem => {
  return (
    typeof record.id === 'string' &&
    typeof record.nodeAttributeId === 'string' &&
    typeof record.definedAt === 'string' &&
    typeof record.readonly === 'boolean'
  )
}

const CompetenciesPageContent = ({ id, rootId }: CompetenciesPageContentProps) => {
  const nodeType = formatUuidToNodeType(id)
  const isNodeRole = nodeType === 'jobcatalog_treerole'
  const isNodeLevel = nodeType === 'jobcatalog_treelevel'

  const [assignParams, setAssignParams] = useState<AssignCompetencyLevelsParams | null>(null)
  const [unassignParams, setUnassignParams] = useState<UnassignCompetencyParams | null>(null)
  const [showFinderModal, setShowFinderModal] = useState(false)

  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [assignAttributes] = useMutation(AssignJobCatalogTreeNodeAttributesDocument, {
    extractorFn: (data) => data.jobCatalogMutations.bulkAssignJobcatalogNodeAttribute,
  })

  const {
    data: pageData,
    loading: loadingPage,
    error: pageError,
  } = useQuery(NodeCompetenciesPageDocument, {
    variables: {
      nodeUuid: id,
      includeRoleLevelNodes: isNodeRole,
    },
  })

  const currentNode = pageData?.jobCatalog?.currentNode?.nodes[0]
  const roleLevelNodes = useMemo(
    () => pageData?.jobCatalog?.roleLevelNodes ?? [],
    [pageData?.jobCatalog?.roleLevelNodes]
  )
  const childrenNodesCount = pageData?.jobCatalog?.childrenNodes?.totalCount ?? 0

  // Derived from the data collection source callbacks below.
  const [totalCompetencies, setTotalCompetencies] = useState(0)
  const [hasAssignedLevels, setHasAssignedLevels] = useState(false)

  useEffect(() => {
    // Reset derived pagination/callout state when changing node.
    setTotalCompetencies(0)
    setHasAssignedLevels(false)
  }, [id])

  const canManageJobCatalogTree = pageData?.jobCatalog._policies.jobCatalogTreeManage.can ?? false
  const canAssignLevels =
    canManageJobCatalogTree && totalCompetencies > 0 && (isNodeLevel || roleLevelNodes.length > 0)

  // Lazy query for the finder modal — only fetches when the modal is open.
  // This query is intentionally separate from NodeCompetenciesPageDocument so the
  // page query no longer holds JobCatalogNodeAttribute entities in the normalized cache.
  // That removes the Apollo re-emission cycle that caused an infinite fetch loop.
  const { data: finderData } = useQuery<
    {
      jobCatalog: {
        nodeAttributesConnection?: { nodes: NodeCompetenciesDataCollectionNodeAttributeFragment[] }
      }
    },
    { nodeUuid: string }
  >(NodeCompetenciesForFinderDocument, {
    skip: !showFinderModal,
    variables: { nodeUuid: id },
    fetchPolicy: 'network-only',
  })
  const allCompetenciesForFinder = useMemo(
    () =>
      mapNodeAttributesToCompetencies(
        finderData?.jobCatalog?.nodeAttributesConnection?.nodes ?? [],
        id
      ),
    [finderData?.jobCatalog?.nodeAttributesConnection?.nodes, id]
  )

  const competencyLevels = getCompetencyLevelsFromNodes(roleLevelNodes ?? [])
  const competencyLevelsByCompetencyId = groupBy(competencyLevels, (l) => l.competencyId)
  // For role nodes: hasAssignedLevels is derived from roleLevelNodes (server data, always accurate).
  // For level nodes: it is updated via mapData below (role nodes are not present for levels).
  const hasAssignedLevelsFromRole = competencyLevels.length > 0
  const roleLevelNodesKey = useMemo(
    () =>
      roleLevelNodes
        .map((node) => {
          const attributesKey =
            node.attributes
              ?.map((attribute) => {
                const levelId = attribute.valueCompetency?.level?.id
                return `${attribute.attributeId}:${levelId ?? ''}`
              })
              .join(',') ?? ''

          return `${node.uuid}:${node.name ?? ''}:${attributesKey}`
        })
        .join('|'),
    [roleLevelNodes]
  )

  const findCompetencyLevelForRoleLevel = (competencyId: string, roleLevelUuid: string) => {
    const levels = competencyLevelsByCompetencyId[competencyId]
    return levels?.find((l) => l.nodeUuid === roleLevelUuid)
  }

  const target: CompetencySuggestionTarget | undefined = useMemo(() => {
    if (!currentNode || !rootId) return

    return {
      type: currentNode.type,
      id: currentNode.id,
      title: currentNode.name ?? '',
      description: currentNode.description ?? '',
      nodeUuid: currentNode.uuid,
      childrenCount: childrenNodesCount,
      treeNodeUuid: rootId,
    }
  }, [currentNode, childrenNodesCount, rootId])

  const buildCompetencyLevelColumns = () => {
    if (isNodeLevel) {
      return [
        {
          label: i18n.t(
            'job_catalog.nodes.competency.data_collection.columns.role_level_expected_level'
          ),
          render: (item: RecordType) => {
            if (!recordIsCompetency(item)) {
              return {
                type: 'longText' as const,
                value: '-',
                full: true,
              }
            }

            return {
              type: 'longText' as const,
              value: item.level ? `${item.level.name} - ${item.level.description}` : '-',
              full: true,
            }
          },
        },
      ]
    }

    const roleLevels = roleLevelNodes ?? []
    return roleLevels.map((roleLevel) => ({
      label: i18n.t('job_catalog.nodes.competency.data_collection.columns.role_expected_level', {
        name: roleLevel.name ?? '',
      }),
      render: (item: RecordType) => {
        if (!recordIsCompetency(item)) {
          return {
            type: 'longText' as const,
            value: {
              text: '-',
              full: true,
            },
          }
        }

        const competencyId = item.id
        const competencyLevel = findCompetencyLevelForRoleLevel(competencyId, roleLevel.uuid)

        return {
          type: 'longText' as const,
          value: {
            text: competencyLevel
              ? `${competencyLevel.name} - ${competencyLevel.description}`
              : '-',
            full: true,
          },
        }
      },
    }))
  }

  const openAssignSidepanel = (competency: CompetencyDataCollectionItem) => {
    if (isNodeRole && roleLevelNodes) {
      setAssignParams({
        competencyId: competency.id,
        roleLevels: roleLevelNodes.map((node) => ({
          node: {
            uuid: node.uuid,
            name: node.name ?? '',
            ancestorName: currentNode?.name ?? undefined,
          },
          competencyLevelId: findCompetencyLevelForRoleLevel(competency.id, node.uuid)?.id,
        })),
      })
    }

    if (isNodeLevel && currentNode) {
      setAssignParams({
        competencyId: competency.id,
        roleLevels: [
          {
            node: { uuid: currentNode.uuid, name: currentNode.name ?? '' },
            competencyLevelId: competency.level?.id,
          },
        ],
      })
    }
  }

  const bulkAssign = async (assignedCompetencies: CompetenciesFinderCompetency[]) => {
    const result = await assignAttributes({
      variables: {
        attributeType: 'competency',
        companyId: company.id,
        nodeUuid: id,
        attributes: buildBulkAssignCompetencyAttributes(
          allCompetenciesForFinder,
          assignedCompetencies
        ),
      },
      refetchQueries: [NodeCompetenciesPageDocument, NodeCompetenciesDataCollectionDocument],
    })

    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.competency.success'),
        time: 5000,
      })
      setShowFinderModal(false)
    }

    track(trackingEvents.jobCatalog.assignCompetencySubmit, {
      event_category: 'Organizations',
      node_id: id,
      node_type: normalizeNodeType(id),
      competency_count: assignedCompetencies.length,
      competency_names: assignedCompetencies.map((c) => c.name).join(', '),
    })
  }

  const assignLevelsLabel = isNodeRole
    ? i18n.t('job_catalog.nodes.competency.data_collection.actions.assign_level.other')
    : i18n.t('job_catalog.nodes.competency.data_collection.actions.assign_level.one')

  const source = useGraphqlDataCollectionSource(
    {
      document: NodeCompetenciesDataCollectionDocument,
      totalItemSummary: (totalItems: number) =>
        i18n.tp('job_catalog.nodes.competency.total_item_summary', { count: totalItems }),
      mapVariables: () => ({ nodeUuid: id }),
      mapData: (data) => {
        const items = mapNodeAttributesToCompetencies(
          data.jobCatalog.nodeAttributesConnection?.nodes ?? [],
          id
        )
        // For level nodes, keep this flag node-wide once we detect assigned levels.
        // It resets on node change via the effect above.
        if (isNodeLevel && items.some((c) => c.level)) {
          setHasAssignedLevels(true)
        }
        return items.map((item) => ({ ...item }))
      },
      mapPagination: (data) => {
        const total = data.jobCatalog.nodeAttributesConnection?.totalCount ?? 0
        setTotalCompetencies(total)
        return { total }
      },
      primaryActions:
        canManageJobCatalogTree && totalCompetencies > 0
          ? () => ({
              label: i18n.t('job_catalog.nodes.competency.data_collection.actions.add_competency'),
              onClick: () => {
                track(trackingEvents.jobCatalog.assignCompetencyStart, {
                  event_category: 'Organizations',
                  node_id: id,
                  node_type: normalizeNodeType(id),
                  from: 'competencies_tab',
                })
                setShowFinderModal(true)
              },
              icon: Plus,
            })
          : undefined,
      itemOnClick: canAssignLevels
        ? (item) => () => {
            if (!recordIsCompetency(item)) return
            openAssignSidepanel(item)
          }
        : undefined,
      itemActions: (item) =>
        compact([
          canAssignLevels && {
            label: assignLevelsLabel,
            icon: Target,
            onClick: () => {
              if (!recordIsCompetency(item)) return
              openAssignSidepanel(item)
            },
          },
          recordIsCompetency(item) &&
            !item.readonly &&
            canManageJobCatalogTree && {
              label: i18n.t('job_catalog.nodes.competency.data_collection.actions.unassign'),
              icon: LinkRemove,
              critical: true,
              onClick: () => {
                track(trackingEvents.jobCatalog.unassignCompetencyStart, {
                  event_category: 'Organizations',
                  node_id: id,
                  node_type: normalizeNodeType(id),
                  competency_name: item.name ?? '',
                })
                setUnassignParams({
                  nodeAttributeId: item.nodeAttributeId,
                  competencyName: item.name ?? '',
                })
              },
            },
        ]),
    },
    // Only stable primitives — Apollo-derived objects are NOT included.
    // NodeCompetenciesPageDocument no longer watches JobCatalogNodeAttribute entities
    // (allCompetencies nodes were removed from that query), so it won't re-emit when
    // the data-collection query writes those entities. No loop, no refs needed.
    [
      id,
      canManageJobCatalogTree,
      canAssignLevels,
      assignLevelsLabel,
      roleLevelNodesKey,
      currentNode?.uuid,
      currentNode?.name,
    ]
  )

  return (
    <ResponsivePage.Body variant="full" loading={loadingPage} error={pageError}>
      <Box gap="s20">
        {canAssignLevels && !(hasAssignedLevelsFromRole || hasAssignedLevels) && (
          <Callout
            description={i18n.t('job_catalog.nodes.competency.assign_levels_callout.description', {
              assign_button_label: assignLevelsLabel,
            })}
            title={i18n.t('job_catalog.nodes.competency.assign_levels_callout.title')}
            type="info"
          />
        )}
        <OneDataCollection
          source={source}
          emptyStates={{
            'no-data': {
              emoji: '🚀',
              title: i18n.t('job_catalog.nodes.empty_state.competency.title'),
              description: i18n.t('job_catalog.nodes.empty_state.competency.description'),
              actions: compact([
                {
                  label: i18n.t('job_catalog.nodes.empty_state.competency.actions.navigate'),
                  onClick: () => navigate(navigation.performanceProcess.fullPath()),
                  variant: 'outline',
                },
                canManageJobCatalogTree
                  ? {
                      label: i18n.t('job_catalog.nodes.empty_state.competency.actions.assign'),
                      onClick: () => {
                        track(trackingEvents.jobCatalog.assignCompetencyStart, {
                          event_category: 'Organizations',
                          node_id: id,
                          node_type: normalizeNodeType(id),
                          from: 'empty_state',
                        })
                        setShowFinderModal(true)
                      },
                      icon: Plus,
                    }
                  : undefined,
              ]),
            },
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t(
                      'job_catalog.nodes.competency.data_collection.columns.competency_name'
                    ),
                    render: (item: RecordType) => ({
                      type: 'text',
                      value: recordIsCompetency(item) ? item.name : undefined,
                    }),
                  },
                  {
                    label: i18n.t(
                      'job_catalog.nodes.competency.data_collection.columns.competency_description'
                    ),
                    render: (item: RecordType) => ({
                      type: 'longText',
                      value: {
                        text: recordIsCompetency(item) ? item.description : undefined,
                        full: true,
                      },
                    }),
                  },
                  ...buildCompetencyLevelColumns(),
                  {
                    label: i18n.t(
                      'job_catalog.nodes.competency.data_collection.columns.inherited_from'
                    ),
                    render: (item: RecordType) => ({
                      type: 'tag',
                      value: {
                        label: recordIsCompetency(item) ? item.definedAt : '',
                        icon: recordIsCompetency(item) ? item.icon : undefined,
                      },
                    }),
                  },
                ],
                frozenColumns: 1,
              },
            },
          ]}
        />
      </Box>

      {showFinderModal && (
        <CompetenciesFinder
          currentCompetencies={allCompetenciesForFinder}
          onSubmit={bulkAssign}
          onClose={() => {
            setShowFinderModal(false)
          }}
          target={target}
        />
      )}

      <CompetencyLevelsAssignSidepanel
        open={Boolean(assignParams)}
        jobCatalogNodeAssignments={assignParams?.roleLevels ?? []}
        competencyId={String(assignParams?.competencyId)}
        onClose={() => setAssignParams(null)}
      />

      {unassignParams && (
        <UnassignCompetencyModal
          {...unassignParams}
          nodeUuid={id}
          onClose={() => setUnassignParams(null)}
        />
      )}
    </ResponsivePage.Body>
  )
}

const CompetenciesPage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = getValidNodeIdFromParams(levelId, roleId, functionId, familyId, rootId)
  const { canSeeCompetencies } = useJobCatalogTreeVisibility()

  useEffect(() => {
    if (!id) return
    track(trackingEvents.jobCatalog.navigateNodeSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(id),
      node_id: id,
      tab: 'competencies',
    })
  }, [id])

  if (!canSeeCompetencies) {
    return (
      <ResponsivePage.Body variant="full">
        <UpsellingKit.EmptyState
          moduleRequested={AvailableModules.competencies}
          featureRequested={Features.competencies_core}
          requestReason={ValidReasons.MissingAddon}
          requestSource="job_catalog_competencies_page"
          title={i18n.t('job_catalog.nodes.empty_state.competency.upsell.title')}
          description={i18n.t('job_catalog.nodes.empty_state.competency.upsell.description')}
          buttonLabel={i18n.t('job_catalog.nodes.empty_state.competency.upsell.button_label')}
          variant="table"
        />
      </ResponsivePage.Body>
    )
  }

  return <CompetenciesPageContent id={id} rootId={rootId} />
}

export default CompetenciesPage
